import {
  calculateAccommodationNights,
  calculateItemTotalCost,
  scaleItemForPax,
} from "@/lib/itineraryCostUtils";

export interface LeaderSourceItem {
  id: string;
  title?: string | null;
  supplier?: string | null;
  item_type?: string | null;
  accommodation_type?: string | null;
  occupancy_type?: string | null;
  room_guest_type?: string | null;
  cost?: number | null;
  manual_price?: number | null;
  quantity?: number | null;
  check_in?: string | null;
  check_out?: string | null;
  service_start?: string | null;
  service_end?: string | null;
  universal_fee_kind?: string | null;
  staff_categories?: string[] | null;
  include_in_quote?: boolean | null;
  leader_excluded?: boolean | null;
}

export interface LeaderRoomLine {
  itemId: string;
  label: string;
  nights: number;
  /** Nightly room rate used for the leader's extra room */
  perNight: number;
  /** Extra rooms added (one per leader) */
  rooms: number;
  /** Calculated line total, before any override */
  total: number;
}

export interface GroupLeaderImpact {
  leaderCount: number;
  payingPax: number;
  roomLines: LeaderRoomLine[];
  /** Accommodation cost added for the leader(s) */
  accommodationTotal: number;
  /** Everything else added for the leader(s) — activities, transfers, meals, etc. */
  otherTotal: number;
  /** Total cost absorbed into the paying travellers */
  netTotal: number;
}

const round2 = (v: number) => Math.round(v * 100) / 100;

/**
 * Accommodation rows that need an extra room when a group leader is added:
 * guest rooms, included in the quote, priced, with a real stay window.
 * Villas / private hire are excluded — they are booked whole, so no extra room.
 */
export function isLeaderRoomEligible(item: LeaderSourceItem): boolean {
  if (item.item_type !== "accommodation") return false;
  if (item.include_in_quote === false) return false;
  if (item.leader_excluded === true) return false;
  if (item.room_guest_type === "staff") return false;
  if ((item.accommodation_type || "").toLowerCase() === "villas") return false;
  if (!item.check_in || !item.check_out) return false;
  if (!(item.cost && item.cost > 0)) return false;
  return true;
}

export const EMPTY_LEADER_IMPACT: GroupLeaderImpact = {
  leaderCount: 0,
  payingPax: 0,
  roomLines: [],
  accommodationTotal: 0,
  otherTotal: 0,
  netTotal: 0,
};

/**
 * Works out what a group leader adds to the programme cost.
 *
 * - per-person parts (activities, transfers, meals, custom entries) are re-costed
 *   from `payingPax` to `payingPax + leaderCount` and the difference taken;
 * - each qualifying property gets one extra room per leader at the same nightly
 *   rate as a guest room, unless an override amount is supplied for that item;
 * - staff rooms, excluded items and items flagged `leader_excluded` are skipped.
 *
 * Nothing is written to the items — this is a pure overlay.
 */
export function computeGroupLeaderImpact(
  items: LeaderSourceItem[],
  options: {
    payingPax: number | null | undefined;
    leaderCount: number | null | undefined;
    /** Item id → line total typed by the user (in the lead currency) */
    roomOverrides?: Record<string, number> | null;
  }
): GroupLeaderImpact {
  const leaderCount = Math.max(0, Math.floor(options.leaderCount || 0));
  const payingPax = Math.max(0, Math.floor(options.payingPax || 0));
  if (!leaderCount || !payingPax) return { ...EMPTY_LEADER_IMPACT };

  const overrides = options.roomOverrides || {};
  const source = (items || []).filter(
    (i) => i.include_in_quote !== false && i.leader_excluded !== true
  );

  let otherTotal = 0;
  for (const item of source) {
    if (item.item_type === "accommodation") continue;
    if (item.room_guest_type === "staff") continue;
    const scaled = scaleItemForPax(item as any, payingPax, payingPax + leaderCount);
    otherTotal += calculateItemTotalCost(scaled as any) - calculateItemTotalCost(item as any);
  }

  const roomLines: LeaderRoomLine[] = source.filter(isLeaderRoomEligible).map((item) => {
    const nights = Math.max(calculateAccommodationNights(item.check_in, item.check_out), 1);
    const perNight = item.cost || 0;
    return {
      itemId: item.id,
      label: item.title || item.supplier || "Accommodation",
      nights,
      perNight,
      rooms: leaderCount,
      total: round2(perNight * nights * leaderCount),
    };
  });

  const accommodationTotal = round2(
    roomLines.reduce((sum, line) => {
      const override = overrides[line.itemId];
      return sum + (override !== undefined && !isNaN(override) ? override : line.total);
    }, 0)
  );

  const other = round2(Math.max(otherTotal, 0));

  return {
    leaderCount,
    payingPax,
    roomLines,
    accommodationTotal,
    otherTotal: other,
    netTotal: round2(accommodationTotal + other),
  };
}

/** Parses the jsonb overrides stored on the lead into a plain number map. */
export function parseLeaderRoomOverrides(raw: unknown): Record<string, number> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const num = typeof value === "number" ? value : parseFloat(String(value));
    if (!isNaN(num)) out[key] = num;
  }
  return out;
}
